import type { ModuleId, ReportData, RelatedPartyEntity } from '../tokens/types';
import type { PdfLayoutOverrides } from '../tokens/pdfLayout';
import { MODULES } from '../modules/moduleRegistry';
import { MODULE_PDF_HEIGHT, PAGE_CONTENT_HEIGHT } from './ReportDocument';

// Lean, no-render diagnostic for failed PDF exports. Replaces the older
// `probePdfFailure` which re-rendered each module individually — that was
// slow and surfaced the same root error twice. This version stays static
// (no react-pdf calls) and prints a structured per-module summary keyed on
// the symptoms we've actually seen ship:
//
//   - "unsupported number: -1.806e+21" → a non-finite number reached a
//     yoga style prop. Skill rule 25. The non-finite usually originates
//     in normalizer math (division, percentages, deltas) — guard with
//     `Number.isFinite()` at the boundary.
//   - "VIEW can't wrap between pages and it's bigger than available page
//     height" → a `wrap={false}` view, OR a tightly-packed bucket inside
//     a wrap-eligible parent, accumulated more content than 660pt of A4
//     content space. Skill rule 26. Force the module onto its own page
//     by bumping its entry in MODULE_PDF_HEIGHT above PAGE_CONTENT_HEIGHT.
//   - Font / glyph errors (`No unicode cmap`) → a string contains a code
//     point the registered font lacks (₹ in GeistMono is the classic).
//     Skill rule 10 — sanitize at the data boundary.
//
// The output is one console.group per failure so it collapses cleanly in
// devtools. Each module that is a likely suspect gets one line with: its
// height estimate, the live data volume, and the rule number that
// matches. The original error stays at the top of the group, untouched,
// so the user can still copy-paste it.

type Suspicion = {
    moduleId: ModuleId;
    moduleTitle: string;
    estimate: number;
    actualSignals: string[]; // human-readable data-volume bullets
    matchedCauses: string[]; // bullet list of likely rule matches
};

// Per-module probes: given the live `ReportData`, return data-volume
// signals (counts that scale module height) and which known-cause rules
// the module is currently a candidate for. Modules we haven't seen crash
// return empty arrays — they show in the summary as "no signals".
const inspectors: Partial<Record<ModuleId, (d: ReportData) => Pick<Suspicion, 'actualSignals' | 'matchedCauses'>>> = {
    relatedParties: (d) => {
        const rp = d.relatedParties;
        const buckets: Array<keyof NonNullable<ReportData['relatedParties']>> = [
            'directors',
            'shareholders',
            'holdings',
            'subsidiaries',
            'associates',
            'jointVentures',
            'sisterConcerns',
            'former',
        ];
        let totalEntities = 0;
        let totalSubRows = 0;
        const filled: string[] = [];
        buckets.forEach((b) => {
            const list = (rp as any)?.[b] as RelatedPartyEntity[] | undefined;
            if (list && list.length) {
                totalEntities += list.length;
                totalSubRows += list.reduce((s, e) => s + (e.otherEntities?.length ?? 0), 0);
                filled.push(`${b}=${list.length}`);
            }
        });
        const rows = totalEntities + totalSubRows + filled.length; // primary rows + sub-rows + bucket headers
        const signals = [
            `buckets filled: ${filled.length} (${filled.join(', ') || 'none'})`,
            `primary entities: ${totalEntities}`,
            `linked sub-rows: ${totalSubRows}`,
            `≈ table rows total: ${rows} (×~22pt = ${rows * 22}pt)`,
        ];
        const causes: string[] = [];
        if (rows * 22 > PAGE_CONTENT_HEIGHT) {
            causes.push(
                `Skill rule 26 — table at ≈${rows * 22}pt exceeds PAGE_CONTENT_HEIGHT (${PAGE_CONTENT_HEIGHT}pt). ` +
                    `Bump MODULE_PDF_HEIGHT.relatedParties above ${PAGE_CONTENT_HEIGHT} so it always gets its own page, and confirm no Box inside the bucket loop has wrap={false}.`,
            );
        }
        return { actualSignals: signals, matchedCauses: causes };
    },
    legalRisk: (d) => {
        const lr = d.legalRisk;
        const amlCount = lr?.amlPepEntities?.length ?? 0;
        const casesAgainst = (lr?.casesAgainst ?? []).reduce((s, g) => s + g.cases.length, 0);
        const casesBy = (lr?.casesBy ?? []).reduce((s, g) => s + g.cases.length, 0);
        const causes: string[] = [];
        if (amlCount > 2 || casesAgainst + casesBy > 12) {
            causes.push(
                `Skill rule 26 — regulatory matrix + cases tables scale with count and can exceed PAGE_CONTENT_HEIGHT (${PAGE_CONTENT_HEIGHT}pt). Module is already pinned to its own page; verify wrap={false} card rows are not over-tall.`,
            );
        }
        return {
            actualSignals: [`AML/PEP entities: ${amlCount}`, `cases against: ${casesAgainst}`, `cases filed by: ${casesBy}`],
            matchedCauses: causes,
        };
    },
    score: (d) => {
        const s = d.score;
        const finite = typeof s?.companyScore === 'number' && Number.isFinite(s.companyScore);
        const causes: string[] = [];
        if (s && !finite) {
            causes.push(
                `Skill rule 25 — companyScore is non-finite (${String(
                    s.companyScore,
                )}). scoreToBand / scaleToTen will return NaN and crash yoga.`,
            );
        }
        return {
            actualSignals: [`companyScore: ${String(s?.companyScore)}`, `headers: ${s?.headers?.length ?? 0}`],
            matchedCauses: causes,
        };
    },
    overview: (d) => {
        const o = d.overview;
        const news = o?.news?.length ?? 0;
        const competitors = o?.competitors?.length ?? 0;
        return {
            actualSignals: [
                `news cards: ${news}`,
                `competitors: ${competitors}`,
                `sector blurb: ${o?.sectorOverview ? `${o.sectorOverview.length} chars` : 'none'}`,
            ],
            matchedCauses: [],
        };
    },
    management: (d) => {
        const m = d.management;
        const profiles = m?.profiles?.length ?? 0;
        return {
            actualSignals: [
                `director profiles: ${profiles} (each emits its own page in ReportDocument)`,
                `momEmployee months: ${m?.momEmployee?.length ?? 0}`,
            ],
            matchedCauses: [],
        };
    },
};

const ERROR_HINTS: Array<{ match: RegExp; rule: string; advice: string }> = [
    {
        match: /unsupported number|-1\.806e\+?21|NaN|Infinity/i,
        rule: 'Skill rule 25',
        advice: 'A non-finite number reached a yoga style prop. Trace recent normalizer math (percentages, deltas, score scaling) and guard each with Number.isFinite(). Universal.Box already substitutes 0 + logs a [Universal] warning — search above for "Non-finite" warnings to find the exact prop name.',
    },
    {
        match: /can't wrap between pages|bigger than available page height/i,
        rule: 'Skill rule 26',
        advice: 'A view with wrap={false} (or a cumulative bucket of children) exceeded PAGE_CONTENT_HEIGHT. Fix by (a) removing wrap={false} from the offending atomic group, (b) bumping MODULE_PDF_HEIGHT[<id>] above 660 so the module gets its own page, or (c) chunking the list at the call site.',
    },
    {
        match: /no unicode cmap|missing.*glyph|font.*not found/i,
        rule: 'Skill rule 10',
        advice: 'A code point lacks a glyph in the registered font (commonly ₹ in GeistMono). Sanitize the source string at the normalizer/parser boundary.',
    },
    {
        match: /failed to fetch|cors|s3\.ap-south-1/i,
        rule: 'Cover-page Image regression',
        advice: 'A cross-origin <Image> failed; once it lands in the page tree, yoga state corrupts and the NEXT module crashes. Revert to text-only wordmark or whitelist the bucket CORS. See PdfCover comment block.',
    },
];

export const logPdfFailureDiagnostic = (
    err: Error,
    data: ReportData,
    enabledModules: ModuleId[],
    layoutOverrides: PdfLayoutOverrides,
): void => {
    if (typeof console === 'undefined') return;
    console.error('[AicaReportV2] PDF generation failed:', err);
    console.groupCollapsed('[AicaReportV2] Diagnostic — known causes + per-module signals');
    try {
        console.info('Org:', data.identity?.orgName ?? '(no orgName)');
        console.info('Layout overrides:', layoutOverrides);
        const matched = ERROR_HINTS.filter((h) => h.match.test(err.message ?? ''));
        if (matched.length === 0) {
            console.info('Error fingerprint: no known rule matched — inspect message above.');
        } else {
            matched.forEach((h) => console.warn(`Likely: ${h.rule}\n${h.advice}`));
        }
        console.group('Per-module data volume vs height estimate');
        let cumulative = 0;
        enabledModules.forEach((id) => {
            const m = MODULES[id as keyof typeof MODULES];
            if (!m || !m.hasData(data)) return;
            const estimate = MODULE_PDF_HEIGHT[id] ?? 280;
            cumulative += estimate;
            const inspector = inspectors[id];
            const probe = inspector?.(data);
            const signals = probe?.actualSignals?.length ? probe.actualSignals.join(' · ') : 'no signals';
            const flag = estimate < PAGE_CONTENT_HEIGHT ? '' : ' [own page]';
            console.info(`${id}: estimate=${estimate}pt${flag} — ${signals}`);
            probe?.matchedCauses.forEach((c) => console.warn(`  ↳ ${c}`));
        });
        console.info(
            `Σ estimates: ${cumulative}pt across ${enabledModules.length} modules (page budget per page: ${PAGE_CONTENT_HEIGHT}pt)`,
        );
        console.groupEnd();
    } finally {
        console.groupEnd();
    }
};
